import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI, dmAPI } from '../services/api';
import notificationSound from '../utils/notificationSound';
import {
  Home,
  HandHeart,
  Users,
  User,
  Shield,
  Heart,
  Gift,
  BookOpen,
  UsersRound,
  MessageSquare
} from 'lucide-react';

const BottomNav = () => {
  const location = useLocation();
  const { isAdmin, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  const prevUnreadCount = useRef(0);
  const prevDmUnreadCount = useRef(0);
  const isFirstLoad = useRef(true);
  const hasPlayedOpenSound = useRef(false);

  // Initialize sound on first user interaction
  useEffect(() => {
    const initSound = () => {
      notificationSound.init();
      document.removeEventListener('click', initSound);
      document.removeEventListener('touchstart', initSound);
    };
    document.addEventListener('click', initSound);
    document.addEventListener('touchstart', initSound);
    return () => {
      document.removeEventListener('click', initSound);
      document.removeEventListener('touchstart', initSound);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadUnreadCount();
      loadDmUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(() => {
        loadUnreadCount();
        loadDmUnreadCount();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Play sound on app open if there are unread notifications
  const playOpenSound = (notificationCount, messageCount) => {
    if (hasPlayedOpenSound.current) return;
    
    const totalUnread = notificationCount + messageCount;
    if (totalUnread > 0) {
      // Small delay to ensure audio context is ready
      setTimeout(() => {
        notificationSound.playNotificationSound();
        hasPlayedOpenSound.current = true;
      }, 500);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      const newCount = data.total || 0;
      
      // Play sound if count increased (and not first load)
      if (!isFirstLoad.current && newCount > prevUnreadCount.current) {
        notificationSound.playNotificationSound();
      }
      
      // On first load, check if we should play open sound
      if (isFirstLoad.current && newCount > 0) {
        playOpenSound(newCount, prevDmUnreadCount.current);
      }
      
      prevUnreadCount.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadDmUnreadCount = async () => {
    try {
      const data = await dmAPI.getUnreadCount();
      const newCount = data.unreadCount || 0;
      
      // Play sound if count increased (and not first load)
      if (!isFirstLoad.current && newCount > prevDmUnreadCount.current) {
        notificationSound.playMessageSound();
      }
      
      // On first load, check if we should play open sound
      if (isFirstLoad.current && newCount > 0) {
        playOpenSound(prevUnreadCount.current, newCount);
      }
      
      prevDmUnreadCount.current = newCount;
      setDmUnreadCount(newCount);
      
      // Mark first load complete after both counts loaded
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }
    } catch (error) {
      console.error('Error loading DM unread count:', error);
    }
  };

  // Core navigation items - most important features (limited for mobile)
  const navItems = [
    { path: '/home', icon: Home, label: 'Home' },
    { path: '/directory', icon: Users, label: 'Directory' },
    { path: '/ministry-teams', icon: UsersRound, label: 'Groups' },
    { path: '/messages', icon: MessageSquare, label: 'Messages' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Shield, label: 'Admin' });
  }

  const handleGiveClick = () => {
    window.open('https://giv.li/0gh8x7', '_blank', 'noopener,noreferrer');
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg z-50">
      <div className="flex items-center justify-around px-1 py-2">
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive = location.pathname === path;
          const showGroupBadge = path === '/ministry-teams' && unreadCount > 0;
          const showMessageBadge = path === '/messages' && dmUnreadCount > 0;
          const badgeCount = showGroupBadge ? unreadCount : (showMessageBadge ? dmUnreadCount : 0);
          
          return (
            <NavLink
              key={path}
              to={path}
              className={`flex flex-col items-center py-1 px-2 rounded-xl transition-colors relative ${
                isActive
                  ? 'text-tan-600'
                  : 'text-gray-400 hover:text-navy-900'
              }`}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                {(showGroupBadge || showMessageBadge) && (
                  <span className="absolute -top-1 -right-2 min-w-[16px] h-4 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </div>
              <span className={`text-[10px] mt-0.5 ${isActive ? 'font-semibold' : ''}`}>
                {label}
              </span>
            </NavLink>
          );
        })}
        {/* Give Button - Opens Givelify */}
        <button
          onClick={handleGiveClick}
          className="flex flex-col items-center py-1 px-2 rounded-xl transition-colors text-tan-600 hover:text-tan-700"
        >
          <Gift className="w-5 h-5 stroke-[2.5]" />
          <span className="text-[10px] mt-0.5 font-semibold">Give</span>
        </button>
      </div>
    </nav>
  );
};

export default BottomNav;
